import React, { useMemo, useState } from 'react';
import { CheckCircle2Icon, MenuIcon, PencilIcon } from 'lucide-react';
import { CourseContentsPanel } from '../components/survey/CourseContentsPanel';
import { SurveyQuestionField } from '../components/survey/SurveyQuestionField';
import { courseOutline, surveyQuestions, surveySections } from '../data/surveyQuestions';
import type { SurveyAnswers } from '../types/survey';
const emptyAnswers: SurveyAnswers = surveyQuestions.reduce((accumulator, question) => ({
  ...accumulator,
  [question.id]: ''
}), {});
export function SurveyPage() {
  const [answers, setAnswers] = useState<SurveyAnswers>(emptyAnswers);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submittedAt, setSubmittedAt] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showContents, setShowContents] = useState(true);
  const requiredQuestions = useMemo(() => surveyQuestions.filter(question => question.required), []);
  const completedRequired = requiredQuestions.filter(question => answers[question.id]?.trim()).length;
  const progress = Math.round(completedRequired / requiredQuestions.length * 100);
  const handleChange = (id: string, value: string) => {
    setAnswers(current => ({
      ...current,
      [id]: value
    }));
    setErrors(current => {
      if (!current[id]) return current;
      const next = {
        ...current
      };
      delete next[id];
      return next;
    });
  };
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    requiredQuestions.forEach(question => {
      if (!answers[question.id]?.trim()) {
        nextErrors[question.id] = 'This question requires an answer.';
      }
    });
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      const firstId = requiredQuestions.find(question => nextErrors[question.id])?.id;
      if (firstId) {
        document.getElementById(`question-${firstId}`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
      return;
    }
    setErrors({});
    setIsSubmitting(true);
    window.setTimeout(() => {
      setIsSubmitting(false);
      setSubmittedAt(new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }));
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 700);
  };
  return <div className="min-h-full bg-gray-50" data-id="element-2898" data-mp="6404jj3s6t24">
      <div className={`mx-auto grid max-w-[1440px] gap-6 px-4 py-6 sm:px-6 sm:py-8 lg:gap-8 lg:px-8 ${showContents ? 'lg:grid-cols-[340px_minmax(0,1fr)]' : 'lg:grid-cols-1'}`} data-id="element-2899" data-mp="ay4b4194wz2o">
        {showContents ? <aside className="lg:sticky lg:top-8 lg:self-start" data-id="element-2900" data-mp="jzaxgqygt43u">
            <CourseContentsPanel sections={courseOutline} activeItemId="survey" onClose={() => setShowContents(false)} data-id="element-2901" data-mp="owayuokddiom" />
          </aside> : <div data-id="element-2902" data-mp="u1et10ipryrf">
            <button type="button" onClick={() => setShowContents(true)} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-2903" data-mp="u0wrpph72ucn">
              <MenuIcon size={16} aria-hidden="true" data-id="element-2904" data-mp="1csft5rvkm0ck" />
              Contents
            </button>
          </div>}

        <section aria-labelledby="survey-title" className="min-w-0" data-id="element-2905" data-mp="180o2lhcezumf">
          <header className="mb-6" data-id="element-2906" data-mp="14042q60gicwl">
            <p className="text-sm font-medium text-gray-500" data-id="element-2907" data-mp="13ysw7yfmmr1s">UOPX Survey</p>
            <h1 id="survey-title" className="mt-1 text-2xl font-bold text-gray-900 sm:text-3xl" data-id="element-2908" data-mp="20ia4u645liv8">
              End of Skill Module Survey
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-gray-600 sm:text-base" data-id="element-2909" data-mp="1g8hbuot00nka">
              Your answers help us shape the next set of skill modules. Thanks for your feedback!
            </p>
          </header>

          {submittedAt ? <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm sm:p-8" data-id="element-2910" data-mp="179wxfzuh101v">
              <div className="flex items-start gap-4" data-id="element-2911" data-mp="1bapbd6eaft4h">
                <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-[#F7FDF5] ring-1 ring-[#E6F7EA]" data-id="element-2912" data-mp="1yzs1l2mzmual">
                  <CheckCircle2Icon size={22} aria-hidden="true" className="text-[#007558]" data-id="element-2913" data-mp="23x9zzz3dpr4c" />
                </span>
                <div className="min-w-0" data-id="element-2914" data-mp="1u7qhiujkvza0">
                  <h2 className="text-lg font-bold text-gray-900 sm:text-xl" data-id="element-2915" data-mp="1u6fb0myr0df7">
                    Thank you — your response has been recorded
                  </h2>
                  <p className="mt-1.5 text-sm text-gray-600" data-id="element-2916" data-mp="2mpvzquheq1u7">
                    Submitted {submittedAt}. You can revise your answers at any
                    time while this module stays open.
                  </p>
                  <button type="button" onClick={() => setSubmittedAt(null)} className="mt-5 inline-flex items-center gap-2 rounded-lg border border-[#00638C] bg-white px-4 py-2 text-sm font-medium text-[#00638C] transition-colors duration-150 hover:bg-[#00638C]/5 focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2" data-id="element-2917" data-mp="68y38vw0tmb8">
                    <PencilIcon size={15} aria-hidden="true" data-id="element-2918" data-mp="29fic0479b8e" />
                    Edit response
                  </button>
                </div>
              </div>
            </div> : <form onSubmit={handleSubmit} noValidate data-id="element-2919" data-mp="3ti31qaivqodb">
              <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm" data-id="element-2920" data-mp="1dn9i16ipm61y">
                <div data-id="element-2921" data-mp="18opwh7812wa4">
                  {surveySections.map((section, sectionIndex) => <section key={section.id} aria-labelledby={`section-${section.id}`} className={sectionIndex > 0 ? 'border-t-8 border-gray-100' : ''} data-id="element-2922" data-mp="1frwevvz6tcih" data-mpi={sectionIndex}>
                      <div className="border-l-4 border-[#00638C] px-4 pb-4 pt-6 sm:px-6" data-id="element-2923" data-mp="1uox863x11fj2">
                        <h2 id={`section-${section.id}`} className="text-lg font-bold text-[#00638C]" data-id="element-2924" data-mp="x2zldubm6o1b">
                          {section.title}
                        </h2>
                        {section.description && <p className="mt-1 max-w-2xl text-sm leading-relaxed text-[#3A4F59]" data-id="element-2925" data-mp="1brcln3hqit1m">
                            {section.description}
                          </p>}
                      </div>
                      <div className="divide-y divide-gray-100" data-id="element-2926" data-mp="j98lldnoqiis">
                        {section.questions.map(question => <SurveyQuestionField key={question.id} question={question} index={surveyQuestions.findIndex(item => item.id === question.id)} value={answers[question.id] ?? ''} error={errors[question.id]} onChange={value => handleChange(question.id, value)} data-id="element-2927" data-mp="2wiqgpw7si633" />)}
                      </div>
                    </section>)}
                </div>

                <div className="flex flex-col gap-4 border-t border-gray-200 bg-gray-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6" data-id="element-2928" data-mp="1uvg0yd4dv5vu">
                  <div className="min-w-0" data-id="element-2929" data-mp="1px61hqbmlj4f">
                    <p className="text-sm font-medium text-gray-700" data-id="element-2930" data-mp="3er3dz4mvu718">
                      {completedRequired} of {requiredQuestions.length} required
                      questions answered
                    </p>
                    <div role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress} aria-label="Survey completion" className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-200 sm:w-56" data-id="element-2931" data-mp="34tfi2bdjithh">
                      <div className="h-full rounded-full bg-[#00638C] transition-[width] duration-300 ease-out" style={{
                    width: `${progress}%`
                  }} data-id="element-2932" data-mp="2m24v25axr0uv" />
                    </div>
                  </div>

                  <button type="submit" disabled={isSubmitting} className="inline-flex flex-none items-center justify-center rounded-lg bg-[#00638C] px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors duration-150 hover:bg-[#004d6d] focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70" data-id="element-2933" data-mp="2h3l3npdkowv4">
                    {isSubmitting ? 'Submitting…' : 'Complete survey'}
                  </button>
                </div>
              </div>
            </form>}
        </section>
      </div>
    </div>;
}